<div class="entity-config-page container-fluid px-0 py-0 py-lg-1">
  <div class="entity-config-shell d-flex flex-column gap-2 mx-auto">
    <section class="entity-config-top rounded-4 overflow-hidden shadow-sm">
      <div class="entity-config-top-hero px-2 px-md-2 py-2">
        <div class="row g-2 align-items-start">
          <div class="col">
            <h1 class="entity-page-title text-white fw-bold lh-sm mb-1">Compliance Management</h1>
            <p class="entity-page-subtitle text-white mb-0">
              Build compliance rules from entity types, bind forms, and keep assigned rule questions in sync with the backend engine.
            </p>
          </div>
          <div class="col-12 col-lg-4">
            <div class="bg-white rounded-small shadow-sm px-3 py-2 small fw-semibold secondary-color">
              Global Rule Library
            </div>
          </div>
        </div>
      </div>

      <div class="px-2 px-md-2 pb-2">
        <section class="row entity-config-top-metrics g-2">
          <div v-for="metric in metrics" :key="metric.label" class="col-12 col-md-4">
            <article class="entity-metric-card h-100 rounded-4 border shadow-sm p-3">
              <div class="d-flex align-items-start justify-content-between gap-3">
                <div>
                  <span class="entity-metric-label secondary-color text-uppercase fw-bold d-block mb-2">
                    {{ metric.label }}
                  </span>
                  <div class="entity-metric-value secondary-color fw-bold lh-1 mb-0">{{ metric.value }}</div>
                </div>
                <div class="entity-metric-icon rounded-3 d-inline-flex align-items-center justify-content-center text-white metric-icon-primary">
                  <i class="bi" :class="metric.icon"></i>
                </div>
              </div>
            </article>
          </div>
        </section>
      </div>
    </section>

    <section class="row g-4 mt-2">
      <div class="col-12 col-xl-5">
        <article class="compliance-card-white rounded-4 border border-purple border-opacity-50 shadow-lg p-3 h-100">
          <div class="d-flex align-items-center gap-3 mb-3">
            <div class="entity-section-icon rounded-3 d-inline-flex align-items-center justify-content-center">
              <i class="bi bi-shield-plus"></i>
            </div>
            <div>
              <div class="entity-section-title fw-bold fs-6">Create Compliance Rule</div>
              <div class="text-secondary small">Matches backend ComplianceRule requirements.</div>
            </div>
          </div>

          <div class="row g-2">
            <div class="col-12">
              <label class="modal-form-label form-label text-xxs">Rule Name</label>
              <input v-model="rule_form.name" type="text" class="form-control form-control-sm rounded-small" placeholder="e.g. Annual KYC Review" />
            </div>
            <div class="col-12">
              <label class="modal-form-label form-label text-xxs">Description</label>
              <textarea v-model="rule_form.description" class="form-control form-control-sm rounded-small" rows="3" placeholder="Describe when this rule should apply"></textarea>
            </div>
            <div class="col-12 col-md-4">
              <label class="modal-form-label form-label text-xxs">Initial Grace Days</label>
              <input v-model.number="rule_form.initial_grace_period_days" type="number" min="1" class="form-control form-control-sm rounded-small" />
            </div>
            <div class="col-12 col-md-4">
              <label class="modal-form-label form-label text-xxs">Frequency Days</label>
              <input v-model.number="rule_form.frequency_days" type="number" min="1" class="form-control form-control-sm rounded-small" placeholder="Optional" />
            </div>
            <div class="col-12 col-md-4">
              <label class="modal-form-label form-label text-xxs">Recurring Grace</label>
              <input v-model.number="rule_form.recurring_grace_period_days" type="number" min="1" class="form-control form-control-sm rounded-small" placeholder="Optional" />
            </div>
            <div class="col-12 col-md-5">
              <label class="modal-form-label form-label text-xxs">Logic</label>
              <select v-model="rule_form.logical_operator" class="form-select form-select-sm rounded-small">
                <option v-for="operator in operators" :key="operator" :value="operator">{{ operator }}</option>
              </select>
            </div>
            <div class="col-12 col-md-7">
              <label class="modal-form-label form-label text-xxs">Trigger Status</label>
              <select v-model="rule_form.trigger_status_id" class="form-select form-select-sm rounded-small">
                <option value="">Select trigger status</option>
                <option v-for="status in form_statuses" :key="status.id" :value="String(status.id)">
                  {{ status.status || status.name || `Status ${status.id}` }}
                </option>
              </select>
            </div>
          </div>

          <button
            type="button"
            class="btn entity-action-btn btn-sm rounded-small shadow-sm fw-semibold text-white mt-3"
            :disabled="saving || loading"
            @click="create_compliance_rule"
          >
            <i class="bi bi-plus-circle-fill me-2"></i>
            {{ saving ? 'Creating...' : 'Create Rule' }}
          </button>
        </article>
      </div>

      <div class="col-12 col-xl-7">
        <article class="compliance-card-white rounded-4 border border-purple border-opacity-50 shadow-lg p-3 h-100">
          <div class="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-2 mb-3">
            <div class="d-flex align-items-center gap-3">
              <div class="entity-section-icon rounded-3 d-inline-flex align-items-center justify-content-center">
                <i class="bi bi-diagram-3"></i>
              </div>
              <div>
                <div class="entity-section-title fw-bold fs-6">Rules & Form Assignment</div>
                <div class="text-secondary small">Assign all questions in a form to the selected compliance rule.</div>
              </div>
            </div>
          </div>

          <div class="row g-2 mb-3">
            <div class="col-12 col-lg-6">
              <label class="modal-form-label form-label text-xxs">Compliance Rule</label>
              <select v-model="selected_rule_id" class="form-select form-select-sm rounded-small">
                <option value="" disabled>Select rule</option>
                <option v-for="rule in filtered_rules" :key="rule.id" :value="String(rule.id)">
                  {{ rule.name }}
                </option>
              </select>
            </div>
            <div class="col-12 col-lg-4">
              <label class="modal-form-label form-label text-xxs">Entity Form</label>
              <select v-model="selected_form_id" class="form-select form-select-sm rounded-small">
                <option value="" disabled>Select form</option>
                <option v-for="form in entity_type_forms" :key="form.id" :value="String(form.id)">
                  {{ form.form_name }}
                </option>
              </select>
            </div>
            <div class="col-12 col-lg-2 d-flex align-items-end">
              <button
                type="button"
                class="btn entity-panel-btn btn-sm rounded-small w-100"
                :disabled="assigning || !selected_rule_id || !selected_form_id"
                @click="assign_selected_form_to_rule"
              >
                {{ assigning ? 'Assigning...' : 'Assign' }}
              </button>
            </div>
          </div>

          <div v-if="selected_rule" class="bg-white rounded-4 border p-3 mb-3">
            <div class="d-flex flex-wrap align-items-center justify-content-between gap-2">
              <div>
                <div class="fw-bold secondary-color">{{ selected_rule.name }}</div>
                <div class="text-secondary small">{{ selected_rule.description || 'No description provided.' }}</div>
              </div>
              <span class="entity-status-pill entity-status-active text-uppercase fw-bold">
                {{ selected_rule.logical_operator || 'AND' }}
              </span>
            </div>
          </div>

          <div class="d-flex flex-column gap-2">
            <div v-if="!assigned_tree.length" class="bg-white rounded-4 border p-4 text-center text-secondary">
              No questions assigned to this rule yet.
            </div>
            <article v-for="form in assigned_tree" :key="form.form_id" class="entity-row-card bg-white rounded-4 border shadow-sm p-3">
              <div class="fw-bold secondary-color mb-2">{{ form.form_name }}</div>
              <div v-for="main in form.main_sections" :key="`${form.form_id}-${main.main_section_id}`" class="mb-2">
                <div class="small fw-semibold">{{ main.main_section_name }}</div>
                <div v-for="sub in main.sub_sections" :key="`${main.main_section_id}-${sub.sub_section_id}`" class="ps-2 mt-1">
                  <div class="text-secondary text-xxs text-uppercase">{{ sub.sub_section_name }}</div>
                  <div class="d-flex flex-column gap-1 mt-1">
                    <div v-for="question in sub.questions" :key="question.id" class="d-flex align-items-center justify-content-between gap-2 border rounded-3 px-2 py-1">
                      <span class="small">{{ question.question_text }}</span>
                      <span class="text-secondary text-xxs">{{ question.question_type }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </article>
          </div>
        </article>
      </div>
    </section>
  </div>
</div>
